/**
 * Ist der laufende Zug schon zu Ende (`done`)? Und was meldete sein Zugvertrag? (15.10.2026, Plan „Verlässliche
 * Abarbeitung“, Welle A)
 *
 * Martin: das Mikro ging mitten im Arbeitszug auf und schickte Raumton und Flüche als neuen Auftrag ab. Ursache (b) des
 * Plans: `voice-v8:tts-audio-end` feuert, sobald IRGENDEINE Vorlese-Schlange leerläuft — auch ein gesprochener Hinweis
 * oder Vorab-Satz während der Werkzeuge (eigene Schlange über playTts). Der Hörer in app.ts fragte nicht, ob der Zug
 * schon fertig war.
 *
 * Jetzt trägt jedes Ende einer Vorlese-Schlange (frontend/lib/tts-queue.ts) den Stand dieses Merkers mit:
 * `zugFertig` und die Meldung `zug` des Servers. Der Hörer öffnet das Mikro nur, wenn `zugFertig` nicht `false` ist.
 *
 * ⚠️ EIN Besitzer je Zug: die Ströme selbst (streamPtt in lib/ptt.ts, streamChat in lib/chat-http.ts) — sie wissen, wann
 *    ihr Zug beginnt, welche Meldung kommt und wann er endet (jedes Ende: done, Fehler, Abriss). Die Kennung schützt vor
 *    dem Ende eines alten Stroms, der erst nach dem Beginn des neuen ankommt.
 * ⚠️ Ohne Zug (Vorlesen per Knopf, Musik, ältere Wege) bleibt `zugFertig` wahr — dort ändert sich nichts.
 */
import { zugMeldungAus, type ZugMeldung } from "../../shared/mikro-nach-antwort.js";

let aktuell = 0;
let laeuft = false;
let meldung: ZugMeldung | null = null;
/** Der Server meldete `schmied-folgt`: Martin sprach beide an, der Schmied antwortet NACH Vespera (server/adressat-reihe.ts). */
let schmiedFolgt = false;
/**
 * 17.10.2026 (Martin: „Fertig“, frontend/lib/fertig.ts): in DIESEM Zug hat Martin die Antwort für abgeschlossen erklärt — und
 * wann. Gehört zum Zug wie die Meldung: ein neuer Zug beginnt ohne.
 */
let fertig = false;
let fertigUm = -Infinity;
/** Beginn des jüngsten Zugs (Client-Uhr) — Schwelle für „frische“ fremde Karten (lib/ja-karte-offen.ts). */
let zugStartUm = 0;
/** Welle 0 (shared/jev-messung.ts): die Kennung der Aufnahme, auf die dieser Zug antwortet — `null` beim Tippen. Nur für die Journalzeilen. */
let zugAz: string | null = null;

/** Die Kennung der Aufnahme des jüngsten Zugs (`null`: keine Aufnahme). */
export function zugAufnahme(): string | null {
  return zugAz;
}

/** Ein Strom beginnt seinen Zug. Rückgabe: die Kennung für `merkeZug` und `zugZuEnde`. */
export function zugBeginnt(aufnahme: string | null = null): number {
  aktuell += 1;
  zugAz = aufnahme;
  laeuft = true;
  zugStartUm = Date.now();
  meldung = null;
  schmiedFolgt = false;
  fertig = false;
  fertigUm = -Infinity;
  return aktuell;
}

/** Die Kennung des jüngsten Zugs — ein Warten (lib/schmied-warten.ts) merkt sie und gilt nach einem neuen Zug nicht mehr. */
export function zugKennung(): number {
  return aktuell;
}

/** Wann begann der jüngste Zug (ms, Client-Uhr)? `null`, wenn noch keiner begann. */
export function zugStartZeit(): number | null {
  return zugStartUm > 0 ? zugStartUm : null;
}

/** Die Meldung `zug` des Servers (roh). Nur für den laufenden Zug. */
export function merkeZug(kennung: number, roh: unknown): void {
  if (kennung === aktuell) meldung = zugMeldungAus(roh);
}

/**
 * 16.10.2026: der Server meldet `schmied-folgt` — nach Vesperas Antwort wartet das Mikro auf den Schmied (app.ts, dasselbe
 * Warten wie nach ihrer Anrede an ihn, lib/schmied-warten.ts). Nur für den laufenden Zug.
 */
export function merkeSchmiedFolgt(kennung: number): void {
  if (kennung === aktuell) schmiedFolgt = true;
}

/** Der Strom ist zu Ende — auf jedem Weg. VOR dem Rückruf `onDone` (der flusht die Schlange; ihr Ende fragt hier). */
export function zugZuEnde(kennung: number): void {
  if (kennung === aktuell) laeuft = false;
}

/** „Fertig“ für den Zug `kennung` (nur den jüngsten — ein alter Zug hat nichts mehr zu sagen). */
export function merkeFertig(kennung: number, jetzt: number = Date.now()): void {
  if (kennung !== aktuell) return;
  fertig = true;
  fertigUm = jetzt;
}

/**
 * Martin beginnt eine neue Aufnahme (lib/ptt.ts `starteAufnahme`): „Fertig“ gilt nur für die ALTE Antwort und endet jetzt —
 * nicht erst beim Senden (`zugBeginnt`). Löscht nur den Merker; öffnet nie ein Mikro, startet nichts. Rückgabe: war es gesetzt?
 */
export function loescheFertig(): boolean {
  const war = fertig;
  fertig = false;
  fertigUm = -Infinity;
  return war;
}

/** Hat Martin im Zug `kennung` „Fertig“ gedrückt? */
export function fertigImZug(kennung: number): boolean {
  return fertig && kennung === aktuell;
}

/** Wie lange ist „Fertig“ in diesem Zug her (ms)? `Infinity`, wenn nicht gedrückt. */
export function fertigSeitMs(jetzt: number = Date.now()): number {
  return fertig ? jetzt - fertigUm : Infinity;
}

/**
 * Was ein Vorlese-Ende mitträgt (Ereignis `voice-v8:tts-audio-end`, detail). `fertig` nur, wenn gedrückt — der Hörer in
 * app.ts hält dann das Mikro zu (shared/mikro-nach-antwort.ts, Grund `fertig-gedrueckt`).
 */
export function zugEndeStand(): { zugFertig: boolean; zug: ZugMeldung | null; schmiedFolgt: boolean; zugStart: number | null; fertig?: true } {
  return { zugFertig: !laeuft, zug: meldung, schmiedFolgt, zugStart: zugStartZeit(), ...(fertig ? { fertig: true as const } : {}) };
}
